// App orchestrator — owns the audio engine and wires it to the UI. Nothing
// audio-related runs until the user clicks Start (Tone.start() unlock).

import * as Tone from "tone";
import { FxRack } from "./audio/fx.ts";
import { Looper, MIC_ID, type LayerSummary, type LooperState } from "./audio/looper.ts";
import { Mixer } from "./audio/mixer.ts";
import { TransportController } from "./audio/transport.ts";
import {
  INSTRUMENTS,
  createInstrument,
  getInstrumentDef,
  type Instrument,
} from "./instruments/registry.ts";
import { KeyboardInput, type PlayContext } from "./input/keyboard.ts";
import { MicDeniedError, MicInput } from "./input/mic.ts";
import { TouchInput } from "./input/touch.ts";
import { SCHEMA_VERSION, createDefaultSong, setSettings, type Song } from "./state/song.ts";
import {
  deleteAudioBlob,
  deleteSong,
  listSongs,
  loadAudioBlob,
  loadSong,
  saveAudioBlob,
  saveSong,
  setLastSong,
  type SerializedLayer,
} from "./state/persistence.ts";
import { MixRecorder, blobToWav, encodeRecording, formatExt, downloadBlob } from "./share/export.ts";
import {
  SHARE_ENABLED,
  fetchProject,
  shareClip,
  shareProject,
  sharedIdFromUrl,
} from "./share/shareClient.ts";
import { dailyPrompt } from "./util/daily.ts";
import { currentWorkspaceId, goToWorkspace, encodeShareHash, readShareHash, clearShareHash } from "./state/router.ts";
import type { LayerMultiple } from "./util/loop.ts";
import { createOnboarding, type Onboarding } from "./ui/onboarding.ts";
import { iconButton } from "./ui/icons.ts";
import { createSwitch, confirmAction } from "./ui/controls.ts";
import { createDailyBanner } from "./ui/dailyBanner.ts";
import { createExportDialog, type ExportDialog, type ExportRequest } from "./ui/exportDialog.ts";
import { mountShell, type ShellApi } from "./ui/shell.ts";
import { createLanes, type Lanes } from "./ui/lanes.ts";
import { createMixerStrip } from "./ui/mixer.ts";
import { createPalette, type Palette } from "./ui/palette.ts";
import { createSettings, type Settings as SettingsModal } from "./ui/settings.ts";
import { createShareDialog, type ShareDialog } from "./ui/shareDialog.ts";
import { createSongMenu, type SongMenu } from "./ui/songMenu.ts";
import { createTransportUI, type TransportUI } from "./ui/transport.ts";
import { Visualizer } from "./visuals/reactive.ts";
import type { NoteEvent, ScaleName } from "./util/types.ts";

const DAILY_DISMISS_KEY = "loopfield:dailyDismissed";

const DEFAULT_INSTRUMENT = "drum-kit";
const MIC_COLOR = "#f78fb3";
const TAP_RESET_MS = 2000;
const SAVE_DEBOUNCE_MS = 400;

export class App {
  private readonly shell: ShellApi;
  private song: Song;
  private started = false;

  private transport: TransportController | null = null;
  private mixer: Mixer | null = null;
  private fx: FxRack | null = null;
  private looper: Looper | null = null;
  private activeInstrument: Instrument | null = null;
  private activeId = DEFAULT_INSTRUMENT;

  private ui: TransportUI | null = null;
  private palette: Palette | null = null;
  private lanes: Lanes | null = null;
  private visualizer: Visualizer | null = null;
  private reducedMotion = false;
  private touch: TouchInput | null = null;
  private keyboard: KeyboardInput | null = null;
  private hintEl: HTMLElement | null = null;
  private performBody: HTMLElement | null = null;
  private micPanel: HTMLElement | null = null;
  private micStatusEl: HTMLElement | null = null;
  private studioEl: HTMLElement | null = null;
  private countinEl: HTMLElement | null = null;

  private mic: MicInput | null = null;

  private mixRecorder: MixRecorder | null = null;
  private exportDialog: ExportDialog | null = null;
  private songMenu: SongMenu | null = null;
  private shareDialog: ShareDialog | null = null;
  private settingsModal: SettingsModal | null = null;
  private onboarding: Onboarding | null = null;
  private masterVolumeDb = 0;

  private tapTimes: number[] = [];
  private saveTimer = 0;
  private quantizeOn = true;
  private transportKeys: ((e: KeyboardEvent) => void) | null = null;

  // Single-level undo for destructive layer actions (delete / clear). The
  // snapshot is the serialized layer list before the action; deleted audio
  // blobs are kept alive (pendingPurge) until the undo is superseded.
  private undoSnapshot: SerializedLayer[] | null = null;
  private pendingPurge: string[] = [];

  // A song opened from a shared #s= link (event layers + settings only).
  private shared: { title: string; settings: Partial<Song["settings"]>; layers: SerializedLayer[] } | null = null;

  constructor(root: HTMLElement) {
    this.song = createDefaultSong(crypto.randomUUID(), Date.now());
    const routeId = currentWorkspaceId();
    const hasRoute = routeId !== null && loadSong(routeId) !== null;
    this.shared = parseShared(readShareHash());
    const label = this.shared ? "Open shared loop" : hasRoute ? "Resume session" : "Start";
    this.shell = mountShell(root, {
      onStart: () => void this.start(),
      onResume: (id) => void this.start(id),
      onRenameTitle: (name) => this.renameSong(this.song.id, name),
      primaryLabel: label,
      recent: listSongs().map((m) => ({ id: m.id, title: m.title, updatedAt: m.updatedAt })),
    });
    if (import.meta.env.DEV) {
      (globalThis as { __lf?: App }).__lf = this;
    }
  }

  /** Dev-only introspection (audio-clock driven; reliable when hidden). */
  get debug(): Record<string, unknown> {
    return {
      looperState: this.looper?.currentState ?? null,
      layerCount: this.looper?.layerCount ?? 0,
      recordedCount: this.looper?.recordedCount ?? 0,
      activeId: this.activeId,
      micOpen: this.mic?.isOpen ?? false,
      bpm: this.song.settings.bpm,
      bars: this.song.settings.bars,
      key: this.song.settings.key,
      scale: this.song.settings.scale,
      latencyMs: this.song.settings.latencyOffsetMs,
      running: this.transport?.isRunning ?? false,
    };
  }

  private async start(explicitId?: string): Promise<void> {
    if (this.started) return;
    await Tone.start();
    this.started = true;
    // Snappier live notes/UI: a tighter scheduling lookahead feels more responsive.
    Tone.getContext().lookAhead = 0.05;

    // Resolve the session. A shared link (no explicit resume) wins and seeds a
    // fresh session; otherwise an explicit recent card, the URL's id, or a new id.
    const shared = !explicitId ? this.shared : null;
    const wantId = explicitId ?? currentWorkspaceId();
    const saved = !shared && wantId ? loadSong(wantId) : null;
    const sessionId = saved?.id ?? (shared ? crypto.randomUUID() : (wantId ?? crypto.randomUUID()));
    goToWorkspace(sessionId); // every session has its own URL
    if (saved) {
      this.song = {
        ...this.song,
        id: saved.id,
        title: saved.title,
        createdAt: saved.createdAt,
        settings: { ...this.song.settings, ...saved.settings },
      };
    } else if (shared) {
      this.song = {
        ...this.song,
        id: sessionId,
        title: shared.title,
        settings: { ...this.song.settings, ...shared.settings },
      };
      clearShareHash();
    } else {
      this.song = { ...this.song, id: sessionId };
    }
    const s = this.song.settings;

    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mixer = new Mixer();
    const fx = new FxRack(Tone.getDestination()); // receives "fx" sends from channels
    this.mixRecorder = new MixRecorder(mixer.masterNode);
    const transport = new TransportController(s.bpm, s.bars, s.metronome, s.metronomeVolume);

    const activeInstrument = makeLiveSafe(createInstrument(this.activeId));
    activeInstrument.init(mixer.masterNode);
    activeInstrument.setScale?.(s.key, s.scale);

    const looper = new Looper(transport, mixer, {
      onState: (state) => this.onLooperState(state),
      onCountIn: (remaining) => {
        this.ui?.setCountIn(remaining);
        if (this.countinEl) this.countinEl.textContent = remaining > 0 ? String(remaining) : "GO";
      },
      onLayersChanged: (layers) => this.onLayersChanged(layers),
      onCapReached: () => this.toast(`Layer limit reached (${16}). Delete a layer to add more.`),
      onLoopBarsChanged: (bars) => {
        this.song = setSettings(this.song, { bars });
        this.ui?.setBars(bars);
        this.updateGrid();
        this.scheduleSave();
      },
    });
    looper.setCountInBars(s.countInBars);
    looper.setGrid(s.quantizeGrid);
    looper.setStrength(s.quantizeStrength);
    looper.setQuantize(true);
    looper.setScale(s.key, s.scale);
    looper.setLatencyOffsetMs(s.latencyOffsetMs);
    looper.setLoopMode(s.loopMode);
    looper.setPersistence({
      saveBlob: (ref, blob) => void saveAudioBlob(ref, blob),
      deleteBlob: (ref) => void deleteAudioBlob(ref),
    });

    const playContext: PlayContext = {
      getActiveDef: () => getInstrumentDef(this.activeId),
      play: (ev) => this.play(ev),
    };
    const keyboard = new KeyboardInput(playContext);
    keyboard.attach();
    // Transport keyboard shortcuts: Space = play/pause, R = record, Cmd/Ctrl+Z = undo.
    this.transportKeys = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      // Undo is the one shortcut that uses a modifier; handle it before the guard.
      if (e.code === "KeyZ" && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
        if (this.undoSnapshot) {
          e.preventDefault();
          void this.undoLast();
        }
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.code === "Space") {
        e.preventDefault();
        this.togglePlay();
      } else if (e.code === "KeyR" && !e.repeat) {
        e.preventDefault();
        this.recordActive();
      }
    };
    document.addEventListener("keydown", this.transportKeys);
    const touch = new TouchInput(playContext, getInstrumentDef(this.activeId));
    touch.setScale(s.key, s.scale);

    const ui = createTransportUI(this.makeTransportHandlers(), {
      bpm: s.bpm,
      bars: s.bars,
      beatsPerBar: transport.beatsPerBar,
      metronome: s.metronome,
      quantize: true,
      key: s.key,
      scale: s.scale,
    });
    const palette = createPalette(
      { onSelect: (id) => this.setActiveInstrument(id) },
      [...INSTRUMENTS.map((d) => ({ id: d.id, name: d.name, color: d.color })), { id: MIC_ID, name: "Mic / Voice", color: MIC_COLOR }],
      this.activeId,
    );
    const lanes = createLanes({
      onMute: (id, muted) => this.looper?.setLayerMute(id, muted),
      onSolo: (id, solo) => this.looper?.setLayerSolo(id, solo),
      onLock: (id, locked) => {
        this.looper?.setLayerLock(id, locked);
        this.scheduleSave();
      },
      onReorder: (id, toIndex) => {
        this.looper?.reorderLayer(id, toIndex);
        this.scheduleSave();
      },
      onVolume: (id, db) => this.onLayerVolume(id, db),
      onPan: (id, pan) => {
        this.looper?.setLayerPan(id, pan);
        this.scheduleSave();
      },
      onSend: (id, amount) => {
        this.looper?.setLayerSend(id, amount);
        this.scheduleSave();
      },
      onDelete: (id) => this.deleteLayerUndoable(id),
      onDuplicate: (id) => void this.looper?.duplicateLayer(id, loadAudioBlob).then(() => this.scheduleSave()),
      onSetLength: (id, m) => {
        this.looper?.setLayerMultiple(id, m as LayerMultiple);
        this.scheduleSave();
      },
      onRename: (id, name) => {
        this.looper?.renameLayer(id, name);
        this.scheduleSave();
      },
      onMarkMove: (id, index, pos) => {
        this.looper?.moveEvent(id, index, pos);
        this.scheduleSave();
      },
      onEmptyRecord: () => this.recordActive(),
    });
    const mixerStrip = createMixerStrip({
      onMasterVolume: (db) => {
        this.masterVolumeDb = db;
        this.mixer?.setMasterVolume(db);
      },
      onReverb: (amt) => this.fx?.setReverb(amt),
      onDelay: (amt) => this.fx?.setDelay(amt),
      onFilter: (hz) => this.fx?.setFilter(hz),
    });
    this.mixer = mixer;
    this.fx = fx;
    this.transport = transport;
    this.activeInstrument = activeInstrument;
    this.looper = looper;
    this.keyboard = keyboard;
    this.touch = touch;
    this.ui = ui;
    this.palette = palette;
    this.lanes = lanes;
    this.updateGrid();

    // Dialogs (appended to the document; opened from Settings).
    this.exportDialog = createExportDialog((req) => void this.exportMix(req));
    this.songMenu = createSongMenu({
      list: () => listSongs(),
      currentId: () => this.song.id,
      onNew: () => this.newSong(),
      onLoad: (id) => void this.loadSongById(id),
      onRename: (id, title) => this.renameSong(id, title),
      onDelete: (id) => void this.deleteSongById(id),
    });
    this.onboarding = createOnboarding();
    // Everything low-frequency lives in one Settings modal (incl. the master Mix).
    this.settingsModal = createSettings(
      {
        onHowItWorks: () => this.onboarding?.open(),
        onSongs: () => this.songMenu?.open(),
        onExport: () => this.exportDialog?.open(),
        onShare: () => void this.doShare(),
        onLoopFirst: (on) => {
          const mode = on ? ("loop-first" as const) : ("tempo-first" as const);
          this.song = setSettings(this.song, { loopMode: mode });
          this.looper?.setLoopMode(mode);
          this.scheduleSave();
        },
        onMultiple: (m) => this.looper?.setNextMultiple(m as LayerMultiple),
      },
      mixerStrip.element,
    );
    const gear = iconButton("sliders", "Settings", "iconbtn iconbtn--ghost");
    gear.addEventListener("click", () => this.settingsModal?.open());
    this.shell.mountTools(gear);
    document.body.append(
      this.exportDialog.element,
      this.songMenu.element,
      this.onboarding.element,
      this.settingsModal.element,
    );
    if (SHARE_ENABLED) {
      this.shareDialog = createShareDialog({
        onShareProject: () => this.doShareProject(),
        onShareClip: () => this.doShareClip(),
      });
      document.body.append(this.shareDialog.element);
    }

    transport.onBeat((b) => ui.pulseBeat(b.beat));
    this.shell.enterStudio(this.buildStudio(ui, lanes, palette, touch));
    this.shell.setLive(true);
    this.shell.setTitle(this.song.title);
    this.updateHint();

    // Daily prompt as a floating toast (outside the layout).
    const dailyToast = this.maybeDailyBanner();
    if (dailyToast) document.body.append(dailyToast);

    transport.start();
    ui.setPlaying(true);

    // Reactive visuals drive the playhead + per-layer/master meters.
    this.visualizer = new Visualizer({
      transport,
      looper,
      lanes,
      mixerStrip,
      getMasterLevel: () => mixer.getMasterLevel(),
      reducedMotion: this.reducedMotion,
    });
    this.visualizer.start();

    // Rebuild saved layers (event + audio from IndexedDB), seed a shared link's
    // layers, or register the new empty session so it appears under "recent".
    const restoreLayers = saved?.layers ?? shared?.layers ?? [];
    if (restoreLayers.length > 0) {
      void looper.restore(restoreLayers, loadAudioBlob).then(() => this.saveNow());
    } else {
      this.saveNow();
    }
    if (shared) this.shared = null;

    // Open a shared project link (only when share is enabled + deployed).
    if (SHARE_ENABLED) {
      const sid = sharedIdFromUrl();
      if (sid) void this.loadSharedProject(sid);
    }

    // First-run guided walkthrough (shows once). Skipped when arriving via a
    // shared link — that visitor just wants to hear the loop, not a tutorial.
    if (!shared) this.onboarding?.maybeAutoOpen();
  }

  private buildStudio(
    ui: TransportUI,
    lanes: Lanes,
    palette: Palette,
    touch: TouchInput,
  ): HTMLElement {
    const studio = document.createElement("div");
    studio.className = "studio";

    const row = document.createElement("div");
    row.className = "stage-row";
    const recBadge = document.createElement("div");
    recBadge.className = "rec-badge";
    recBadge.innerHTML = `<span class="rec-badge__dot"></span>REC`;
    row.append(lanes.element, recBadge);

    // Big count-in number overlaid on the stage.
    const countin = document.createElement("div");
    countin.className = "countin";
    const countinNum = document.createElement("div");
    countinNum.className = "countin__num";
    countin.append(countinNum);
    this.countinEl = countinNum;
    this.studioEl = studio;
    studio.dataset["rec"] = "idle";

    const perform = document.createElement("div");
    perform.className = "perform";
    const hint = document.createElement("div");
    hint.className = "perform__hint";
    this.hintEl = hint;
    const body = document.createElement("div");
    body.className = "perform__body";
    body.append(touch.element);
    this.performBody = body;
    this.micPanel = this.buildMicPanel();
    perform.append(palette.element, hint, body);

    studio.append(ui.element, row, perform, countin);
    return studio;
  }

  private buildMicPanel(): HTMLElement {
    const panel = document.createElement("div");
    panel.className = "mic-panel";

    const status = document.createElement("div");
    status.className = "mic-panel__status";
    status.textContent = "Mic ready. Press Record to capture a loop.";
    this.micStatusEl = status;

    const controls = document.createElement("div");
    controls.className = "mic-panel__controls";

    // Monitor toggle (hear yourself; use headphones to avoid feedback)
    const monitor = createSwitch(
      "Monitor",
      false,
      (on) => this.mic?.setMonitor(on),
      "Hear your mic live (use headphones)",
    ).el;

    // Latency slider
    const lat = document.createElement("label");
    lat.className = "mic-panel__latency";
    const latLabel = document.createElement("span");
    latLabel.textContent = `Latency ${this.song.settings.latencyOffsetMs} ms`;
    const latSlider = document.createElement("input");
    latSlider.type = "range";
    latSlider.min = "0";
    latSlider.max = "300";
    latSlider.step = "5";
    latSlider.value = String(this.song.settings.latencyOffsetMs);
    latSlider.className = "slider";
    latSlider.addEventListener("input", () => {
      const ms = Number(latSlider.value);
      latLabel.textContent = `Latency ${ms} ms`;
      this.looper?.setLatencyOffsetMs(ms);
      this.song = setSettings(this.song, { latencyOffsetMs: ms });
      this.scheduleSave();
    });
    lat.append(latLabel, latSlider);

    controls.append(monitor, lat);

    const note = document.createElement("p");
    note.className = "mic-panel__note";
    note.textContent = "Use headphones when monitoring to avoid feedback.";

    panel.append(status, controls, note);
    return panel;
  }

  private togglePlay(): void {
    const t = this.transport;
    if (!t) return;
    if (t.isRunning) {
      t.stop();
      this.ui?.setPlaying(false);
    } else {
      t.start();
      this.ui?.setPlaying(true);
    }
  }

  private recordActive(): void {
    if (this.activeId === MIC_ID) {
      if (!this.mic?.isOpen) {
        void this.openMic();
        return;
      }
      this.looper?.record(MIC_ID);
    } else {
      this.looper?.record(this.activeId);
    }
  }

  /** Confirm before wiping every layer (locked layers survive). */
  private async confirmClear(): Promise<void> {
    const count = this.looper?.layerIds().length ?? 0;
    if (count === 0) return;
    const ok = await confirmAction({
      title: "Clear all layers?",
      body: `This removes all ${count} layer${count === 1 ? "" : "s"} in this session. Locked layers are kept.`,
      confirmLabel: "Clear all",
      danger: true,
    });
    if (ok) this.runUndoable("Layers cleared", () => this.looper?.clear(true));
  }

  private deleteLayerUndoable(id: string): void {
    this.runUndoable("Layer deleted", () => this.looper?.deleteLayer(id, true));
  }

  // Snapshot before a destructive op, keep its audio blobs alive for undo, and
  // purge the previously-kept blobs (that undo is now lost).
  private runUndoable(label: string, mutate: () => void): void {
    if (!this.looper) return;
    const before = this.looper.serialize();
    this.looper.purgeBlobs(this.pendingPurge);
    this.pendingPurge = [];
    mutate();
    const afterRefs = new Set(
      this.looper.serialize().filter((l) => l.kind === "audio").map((l) => (l as { audioRef: string }).audioRef),
    );
    this.pendingPurge = before
      .filter((l) => l.kind === "audio" && !afterRefs.has((l as { audioRef: string }).audioRef))
      .map((l) => (l as { audioRef: string }).audioRef);
    this.undoSnapshot = before;
    this.showUndo(label);
    this.scheduleSave();
  }

  private async undoLast(): Promise<void> {
    const snap = this.undoSnapshot;
    if (!snap || !this.looper) return;
    this.undoSnapshot = null;
    this.pendingPurge = []; // restoring → keep those blobs
    this.looper.clear(true);
    await this.looper.restore(snap, loadAudioBlob);
    this.saveNow();
    this.toast("Restored");
  }

  private showUndo(label: string): void {
    const el = document.createElement("div");
    el.className = "toast toast--undo";
    el.setAttribute("role", "status");
    const text = document.createElement("span");
    text.textContent = label;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "toast__undo";
    btn.textContent = "Undo";
    let gone = false;
    const dismiss = (): void => {
      if (gone) return;
      gone = true;
      el.classList.add("is-out");
      window.setTimeout(() => el.remove(), 400);
    };
    btn.addEventListener("click", () => {
      void this.undoLast();
      dismiss();
    });
    el.append(text, btn);
    document.body.append(el);
    window.setTimeout(dismiss, 6000);
  }

  private play(ev: NoteEvent): void {
    this.activeInstrument?.trigger(ev);
    this.looper?.capture(ev);
  }

  private setActiveInstrument(id: string): void {
    if (id === this.activeId) return;
    if (id === MIC_ID) {
      this.selectMic();
      return;
    }
    this.activeId = id;
    this.activeInstrument?.dispose();
    const def = getInstrumentDef(id);
    const instrument = makeLiveSafe(createInstrument(id));
    instrument.init(this.mixer!.masterNode);
    instrument.setScale?.(this.song.settings.key, this.song.settings.scale);
    this.activeInstrument = instrument;
    this.touch?.setInstrument(def);
    if (this.performBody && this.touch) this.performBody.replaceChildren(this.touch.element);
    this.palette?.setActive(id);
    this.updateHint();
  }

  private selectMic(): void {
    this.activeId = MIC_ID;
    this.activeInstrument?.dispose();
    this.activeInstrument = null;
    this.palette?.setActive(MIC_ID);
    if (this.performBody && this.micPanel) this.performBody.replaceChildren(this.micPanel);
    this.updateHint();
    void this.openMic();
  }

  private async openMic(): Promise<void> {
    if (this.mic?.isOpen) return;
    this.setMicStatus("Requesting microphone…");
    const mic = new MicInput();
    this.mic = mic;
    try {
      await mic.open();
      this.looper?.setAudioRecorder(mic);
      this.setMicStatus("Mic ready. Press Record to capture a loop.");
    } catch (err) {
      this.mic = null;
      this.looper?.setAudioRecorder(null);
      this.setMicStatus(
        err instanceof MicDeniedError
          ? "Microphone blocked. Allow it in your browser to record voice."
          : "Couldn't open the microphone.",
      );
    }
  }

  private setMicStatus(text: string): void {
    if (this.micStatusEl) this.micStatusEl.textContent = text;
  }

  private updateHint(): void {
    if (!this.hintEl) return;
    if (this.activeId === MIC_ID) {
      this.hintEl.textContent = "Mic / Voice · sing or beatbox, then Record";
      return;
    }
    const def = getInstrumentDef(this.activeId);
    this.hintEl.textContent = `${def.name} · keys ${def.playHint}`;
  }

  private makeTransportHandlers() {
    return {
      onPlayToggle: () => this.togglePlay(),
      onRecord: () => this.recordActive(),
      onClear: () => void this.confirmClear(),
      onBpmChange: (bpm: number) => {
        this.transport?.setBpm(bpm);
        this.song = setSettings(this.song, { bpm });
        this.ui?.setBpm(bpm);
        this.scheduleSave();
      },
      onBarsChange: (bars: number) => {
        this.transport?.setBars(bars);
        this.song = setSettings(this.song, { bars });
        this.ui?.setBars(bars);
        this.updateGrid();
        this.scheduleSave();
      },
      onMetronomeToggle: (on: boolean) => {
        this.transport?.setMetronome(on);
        this.song = setSettings(this.song, { metronome: on });
        this.scheduleSave();
      },
      onQuantizeToggle: (on: boolean) => {
        this.quantizeOn = on;
        this.looper?.setQuantize(on);
        this.updateGrid();
      },
      onTapTempo: () => this.handleTapTempo(),
      onKeyChange: (key: string) => {
        this.song = setSettings(this.song, { key });
        this.applyScale();
        this.scheduleSave();
      },
      onScaleChange: (scale: ScaleName) => {
        this.song = setSettings(this.song, { scale });
        this.applyScale();
        this.scheduleSave();
      },
    };
  }

  private applyScale(): void {
    const { key, scale } = this.song.settings;
    this.activeInstrument?.setScale?.(key, scale);
    this.looper?.setScale(key, scale);
    this.touch?.setScale(key, scale);
  }

  private toast(message: string): void {
    const el = document.createElement("div");
    el.className = "toast";
    el.setAttribute("role", "status");
    el.textContent = message;
    document.body.append(el);
    window.setTimeout(() => el.classList.add("is-out"), 2600);
    window.setTimeout(() => el.remove(), 3100);
  }

  private handleTapTempo(): void {
    const now = performance.now();
    if (this.tapTimes.length && now - (this.tapTimes.at(-1) ?? 0) > TAP_RESET_MS) {
      this.tapTimes = [];
    }
    this.tapTimes.push(now);
    if (this.tapTimes.length > 4) this.tapTimes.shift();
    if (this.tapTimes.length < 2) return;

    let total = 0;
    for (let i = 1; i < this.tapTimes.length; i++) {
      total += (this.tapTimes[i] ?? 0) - (this.tapTimes[i - 1] ?? 0);
    }
    const bpm = Math.max(50, Math.min(200, Math.round(60000 / (total / (this.tapTimes.length - 1)))));
    this.transport?.setBpm(bpm);
    this.song = setSettings(this.song, { bpm });
    this.ui?.setBpm(bpm);
    this.scheduleSave();
  }

  private onLayerVolume(id: string, db: number): void {
    this.looper?.setLayerVolume(id, db);
    this.scheduleSave();
  }

  /** Push the current bars/beats/quant into the lanes' timeline grid. */
  private updateGrid(): void {
    if (!this.lanes || !this.transport) return;
    const g = this.song.settings.quantizeGrid;
    const quantDivs = g === "16n" ? 4 : g === "8n" ? 2 : 1;
    this.lanes.setGrid({
      bars: this.song.settings.bars,
      beatsPerBar: this.transport.beatsPerBar,
      quantDivs,
      quantOn: this.quantizeOn,
    });
  }

  private onLooperState(state: LooperState): void {
    this.ui?.setRecordState(state);
    if (this.studioEl) this.studioEl.dataset["rec"] = state;
    if (state !== "counting" && this.countinEl) this.countinEl.textContent = "";
  }

  private onLayersChanged(layers: LayerSummary[]): void {
    this.lanes?.setLayers(layers);
    this.ui?.setBarsEnabled(layers.length === 0);
    this.scheduleSave();
  }

  private scheduleSave(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => this.saveNow(), SAVE_DEBOUNCE_MS);
  }

  private saveNow(): void {
    if (!this.looper) return;
    saveSong({
      schemaVersion: SCHEMA_VERSION,
      id: this.song.id,
      title: this.song.title,
      createdAt: this.song.createdAt,
      updatedAt: Date.now(),
      settings: this.song.settings,
      layers: this.looper.serialize(),
    });
    setLastSong(this.song.id);
  }

  // --- Songs (save/load) --------------------------------------------------
  private newSong(): void {
    this.discardUndo();
    this.looper?.clear();
    this.song = { ...this.song, id: crypto.randomUUID(), title: "Untitled", createdAt: Date.now() };
    this.shell.setTitle(this.song.title);
    this.saveNow();
  }

  /** Drop the pending undo and permanently delete its kept-alive blobs. */
  private discardUndo(): void {
    this.looper?.purgeBlobs(this.pendingPurge);
    this.pendingPurge = [];
    this.undoSnapshot = null;
  }

  private async loadSongById(id: string): Promise<void> {
    const s = loadSong(id);
    if (!s || !this.looper || !this.transport) return;
    this.discardUndo();
    this.looper.clear();
    this.song = {
      ...this.song,
      id: s.id,
      title: s.title,
      createdAt: s.createdAt,
      settings: { ...this.song.settings, ...s.settings },
    };
    this.applyLoadedSettings();
    this.shell.setTitle(this.song.title);
    await this.looper.restore(s.layers, loadAudioBlob);
    this.saveNow();
  }

  private renameSong(id: string, title: string): void {
    if (id === this.song.id) {
      this.song = { ...this.song, title };
      this.shell.setTitle(title);
      this.saveNow();
    } else {
      const s = loadSong(id);
      if (s) saveSong({ ...s, title, updatedAt: Date.now() });
    }
  }

  private async deleteSongById(id: string): Promise<void> {
    await deleteSong(id);
    if (id === this.song.id) this.newSong();
  }

  private applyLoadedSettings(): void {
    const st = this.song.settings;
    this.transport?.setBpm(st.bpm);
    this.transport?.setBars(st.bars);
    this.transport?.setMetronome(st.metronome);
    this.looper?.setScale(st.key, st.scale);
    this.looper?.setLatencyOffsetMs(st.latencyOffsetMs);
    this.activeInstrument?.setScale?.(st.key, st.scale);
    this.touch?.setScale(st.key, st.scale);
    this.ui?.setBpm(st.bpm);
    this.ui?.setBars(st.bars);
    this.ui?.setMetronome(st.metronome);
    this.ui?.setKeyScale(st.key, st.scale);
  }

  // --- Daily prompt -------------------------------------------------------
  private maybeDailyBanner(): HTMLElement | null {
    const prompt = dailyPrompt(new Date());
    let dismissed: string | null = null;
    try {
      dismissed = localStorage.getItem(DAILY_DISMISS_KEY);
    } catch {
      dismissed = null;
    }
    if (dismissed === prompt.dateKey) return null;
    return createDailyBanner(prompt, {
      onAccept: () => {
        this.song = setSettings(this.song, { key: prompt.key, scale: prompt.scale });
        this.applyScale();
        this.ui?.setKeyScale(prompt.key, prompt.scale);
        this.scheduleSave();
      },
      onDismiss: () => {
        try {
          localStorage.setItem(DAILY_DISMISS_KEY, prompt.dateKey);
        } catch {
          // ignore
        }
      },
    });
  }

  // --- Export -------------------------------------------------------------
  private async exportMix(req: ExportRequest): Promise<void> {
    const t = this.transport;
    const rec = this.mixRecorder;
    const dlg = this.exportDialog;
    const mixer = this.mixer;
    if (!t || !rec || !dlg || !mixer) return;
    if (!t.isRunning) {
      t.start();
      this.ui?.setPlaying(true);
    }
    const ext = formatExt(req.format);

    // Stems: capture each layer in isolation (soloed) and download one file each.
    if (req.stems) {
      const layers = this.looper?.serialize() ?? [];
      if (layers.length === 0) {
        dlg.setResult("No layers to export", 0);
        return;
      }
      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i]!;
        mixer.setSolo(layer.id, true);
        const blob = await rec.capture({
          loops: req.loops,
          loopDurSec: t.loopDurationSec,
          fade: false,
          onProgress: (p) => dlg.setProgress((i + p) / layers.length),
        });
        mixer.setSolo(layer.id, false);
        const out = await encodeRecording(blob, req.format);
        downloadBlob(out, `loopfield-${slug(this.song.title)}-${slug(layer.name)}.${ext}`);
      }
      dlg.setResult(`${layers.length} stem${layers.length === 1 ? "" : "s"} downloaded (.${ext})`, 0);
      return;
    }

    const blob = await rec.capture(
      { loops: req.loops, loopDurSec: t.loopDurationSec, fade: req.fade, onProgress: (p) => dlg.setProgress(p) },
      (sec) => this.mixer?.rampMasterVolume(-60, sec),
    );
    if (req.fade) this.mixer?.setMasterVolume(this.masterVolumeDb); // restore after fade
    const out = await encodeRecording(blob, req.format);
    const name = `loopfield-${slug(this.song.title)}-${Math.round(t.getBpm())}bpm.${ext}`;
    downloadBlob(out, name);
    dlg.setResult(name, out.size);
  }

  /** Copy a backendless share link (event layers + settings in the URL hash). */
  private async doShare(): Promise<void> {
    const all = this.looper?.serialize() ?? [];
    const events = all.filter((l) => l.kind === "event");
    const url = encodeShareHash({ v: SCHEMA_VERSION, t: this.song.title, s: this.song.settings, l: events });
    const note = all.some((l) => l.kind === "audio") ? " Voice layers aren't included in links." : "";
    try {
      await navigator.clipboard.writeText(url);
      this.toast(`Share link copied.${note}`);
    } catch {
      this.toast("Couldn't copy the link automatically.");
    }
  }

  // --- Share (legacy Worker path, only reachable when SHARE_ENABLED) -------
  private async doShareProject(): Promise<string> {
    const json = {
      schemaVersion: SCHEMA_VERSION,
      id: this.song.id,
      title: this.song.title,
      createdAt: this.song.createdAt,
      updatedAt: Date.now(),
      settings: this.song.settings,
      layers: (this.looper?.serialize() ?? []).filter((l) => l.kind === "event"),
    };
    return (await shareProject(json)).url;
  }

  private async doShareClip(): Promise<string> {
    const t = this.transport;
    const rec = this.mixRecorder;
    if (!t || !rec) throw new Error("not ready");
    if (!t.isRunning) {
      t.start();
      this.ui?.setPlaying(true);
    }
    const blob = await rec.capture(
      { loops: 2, loopDurSec: t.loopDurationSec, fade: true },
      (sec) => this.mixer?.rampMasterVolume(-60, sec),
    );
    this.mixer?.setMasterVolume(this.masterVolumeDb);
    return shareClip(await blobToWav(blob));
  }

  private async loadSharedProject(sid: string): Promise<void> {
    try {
      const json = (await fetchProject(sid)) as { settings?: Partial<Song["settings"]>; layers?: SerializedLayer[] };
      if (!this.looper || !json.layers) return;
      this.looper.clear();
      if (json.settings) this.song = { ...this.song, settings: { ...this.song.settings, ...json.settings } };
      this.applyLoadedSettings();
      await this.looper.restore(json.layers, loadAudioBlob);
    } catch {
      // bad/expired link — ignore
    }
  }

  /** Tear down the engine and listeners (kept for an eventual reset). */
  dispose(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    if (this.transportKeys) document.removeEventListener("keydown", this.transportKeys);
    this.visualizer?.stop();
    this.keyboard?.detach();
    this.mic?.close();
    this.mixRecorder?.dispose();
    this.looper?.dispose();
    this.activeInstrument?.dispose();
    this.fx?.dispose();
    this.transport?.dispose();
    this.mixer?.dispose();
  }
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "loop";
}

/** Validate a decoded #s= share payload into a usable seed (or null). */
function parseShared(
  raw: unknown,
): { title: string; settings: Partial<Song["settings"]>; layers: SerializedLayer[] } | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as { t?: unknown; s?: unknown; l?: unknown };
  const layers = Array.isArray(o.l) ? (o.l as SerializedLayer[]) : [];
  if (layers.length === 0) return null;
  const title = typeof o.t === "string" && o.t.trim() ? o.t : "Shared loop";
  const settings = o.s && typeof o.s === "object" ? (o.s as Partial<Song["settings"]>) : {};
  return { title, settings, layers };
}

// Live triggers (keyboard, touch, gesture, theremin) pass no explicit time, so
// each resolves to Tone.now() inside the instrument. Two notes in the same JS
// tick — a chord, a fast run, or any quick retrigger — then share an identical
// time, and Tone throws "start time must be strictly greater than previous",
// silently dropping the note. Wrapping the active instrument hands every
// time-less trigger a strictly-increasing, near-now time; a few ms of spread is
// inaudible but keeps every note sounding.
function makeLiveSafe(inst: Instrument): Instrument {
  let last = 0;
  const nextTime = (): number => {
    const now = Tone.now();
    const t = now > last ? now : last + 0.003;
    last = t;
    return t;
  };
  const wrapped: Instrument = {
    id: inst.id,
    name: inst.name,
    category: inst.category,
    archetype: inst.archetype,
    scaleLocked: inst.scaleLocked,
    polyphony: inst.polyphony,
    init: (output) => inst.init(output),
    trigger: (ev, time) => inst.trigger(ev, time ?? nextTime()),
    dispose: () => inst.dispose(),
  };
  if (inst.attack) wrapped.attack = (ev, time) => inst.attack!(ev, time ?? nextTime());
  if (inst.release) wrapped.release = (ev, time) => inst.release!(ev, time ?? nextTime());
  if (inst.setScale) wrapped.setScale = (k, s) => inst.setScale!(k, s);
  if (inst.setMacro) wrapped.setMacro = (v) => inst.setMacro!(v);
  return wrapped;
}
